// ASK-163 Client 4s item 1a — the HUD safe zone: no HUD control covers an open sheet.
//
// Vanessa, BETA001b (prod, phone, landscape): the merchant's Edit switch sat over the cart's ✕ and the
// Report sheet's title. Measured on dev 9776c75 (probe-phone-walk P18m): the Play/Edit switch and the
// Studio's catalogue notice are `position: fixed` on <body> at z 2147483000, while every sheet lives
// inside the store's own stacking context (`.kapowx-store`, z 9900) — so no z-index on a sheet can ever
// rise above body-level chrome. Raising z wars would only move the collision.
//
// The rule, from the AAA HUD precedent (Fortnite / Roblox: an open menu owns its rectangle; the HUD that
// would sit on it steps aside until it closes): every HUD element — tagged `data-hud` where it is MADE —
// whose rectangle overlaps an open sheet — tagged `data-sheet` — yields (`data-hud-yield`, hidden and
// untouchable by CSS) for as long as that sheet is open. Geometry, not a list of pairs: a bottom sheet in
// portrait leaves the top-right cluster alone, a right-docked landscape sheet moves exactly the controls
// over it, a modal scrim moves all of them, and a new sheet or a new HUD control needs only its tag.
// A sheet may name HUD it never displaces in `data-sheet-skip` (a selector list) — the Studio panel is a sheet
// (ASK-177 §10 item 7: an open editor panel owns its rectangle, as in every AAA editor) but its own furniture —
// the edit legend, the Play/Edit switch (the only way back) — stays.

/** Strict rectangle overlap (shared edges are not an overlap). */
export function overlaps(a, b) {
	return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

/**
 * @param {{id: string, rect: {left:number,top:number,right:number,bottom:number}}[]} hud
 * @param {({left:number,top:number,right:number,bottom:number} | {rect: object, skip?: string[]})[]} sheets  open sheets:
 *        a bare rect, or `{ rect, skip }` where `skip` names HUD ids that never yield to THIS sheet (itself — a toast is a
 *        sheet to the HUD and HUD to a modal — or what it contains)
 * @returns {Set<string>} ids of the HUD elements that must yield
 */
export function hudYields(hud, sheets) {
	const out = new Set();
	const S = sheets.map((s) => (s && s.rect ? s : { rect: s, skip: [] }));
	for (const h of hud) {
		const r = h.rect;
		if (!(r.right > r.left && r.bottom > r.top)) continue;   // not drawn
		if (S.some((s) => !(s.skip || []).includes(h.id) && overlaps(r, s.rect))) out.add(h.id);
	}
	return out;
}

const shown = (el) => {
	if (!el.isConnected || el.closest("[hidden]")) return false;
	const cs = getComputedStyle(el);
	return cs.display !== "none" && cs.visibility !== "hidden" && +cs.opacity > 0;
};

/**
 * Watch the page and keep `data-hud-yield` true to the rule. Re-decided on any class/hidden change, on
 * resize/orientation, and at the end of a transition (a sliding sheet's final rectangle). Idempotent.
 */
export function mountHudSafeZone(doc = typeof document !== "undefined" ? document : null) {
	if (!doc || doc.__kxHudSafeZone) return doc?.__kxHudSafeZone || null;
	const win = doc.defaultView;
	let queued = false;
	const decide = () => {
		queued = false;
		const vw = win.innerWidth, vh = win.innerHeight;
		const els = [...doc.querySelectorAll("[data-hud]")];
		// A yielded element keeps its box (visibility, not display), so its rect is still the one to judge.
		const hud = els.map((el, i) => ({ id: String(i), el, rect: el.closest("[hidden]") ? { left: 0, top: 0, right: 0, bottom: 0 } : el.getBoundingClientRect() }));
		const sheets = [...doc.querySelectorAll("[data-sheet]")].filter(shown).map((sh) => {
			const r = sh.getBoundingClientRect();
			// never yield to itself or to a sheet it is part of (or that is part of it) — nor to a sheet that names it in
			// `data-sheet-skip` (a selector list: the Studio panel keeps the edit legend and the Play/Edit switch, ASK-177 §10)
			const keep = sh.getAttribute("data-sheet-skip");
			const named = (el) => { try { return !!keep && el.matches(keep); } catch { return false; } };
			const skip = hud.filter((h) => h.el === sh || h.el.contains(sh) || sh.contains(h.el) || named(h.el)).map((h) => h.id);
			return { rect: { left: Math.max(0, r.left), top: Math.max(0, r.top), right: Math.min(vw, r.right), bottom: Math.min(vh, r.bottom) }, skip };
		}).filter((s) => s.rect.right > s.rect.left && s.rect.bottom > s.rect.top);
		const y = hudYields(hud, sheets);
		for (const h of hud) {
			const want = y.has(h.id);
			if (want !== h.el.hasAttribute("data-hud-yield")) h.el.toggleAttribute("data-hud-yield", want);
		}
	};
	const queue = () => { if (!queued) { queued = true; win.requestAnimationFrame(decide); } };
	const mo = new win.MutationObserver(queue);
	mo.observe(doc.body, { subtree: true, childList: true, attributes: true, attributeFilter: ["class", "hidden"] });
	win.addEventListener("resize", queue);
	win.addEventListener("orientationchange", queue);
	doc.addEventListener("transitionend", queue, true);
	queue();
	const api = { decide, destroy: () => { mo.disconnect(); win.removeEventListener("resize", queue); win.removeEventListener("orientationchange", queue); doc.removeEventListener("transitionend", queue, true); doc.__kxHudSafeZone = null; } };
	doc.__kxHudSafeZone = api;
	return api;
}
